/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { IPriceComponentResources, ProductComponent } from '@msdyn365-commerce/components';
import { ICoreContext, IImageSettings } from '@msdyn365-commerce/core';
import { ProductAvailableQuantity, ProductSearchResult, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { ICartState } from '@msdyn365-commerce/global-state';

import * as React from 'react';

import { IRsmSearchResultContainerResources } from '../rsm-search-result-container.props.autogenerated';

interface IProductSearchResultItems {
    products: ProductSearchResult[];
    context: ICoreContext;
    resources: IRsmSearchResultContainerResources;
    imageSettings?: IImageSettings;
    moduleType: string;
    moduleId: string;
    allowBackNavigation?: boolean;
    telemetryContent: ITelemetryContent;
    quickviewSlot?: {} | null | undefined;
    productComparisonButton?: {} | null | undefined;
    channelInventoryConfigurationId?: number;
    isPriceMinMaxEnabled?: boolean;
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    cart?: ICartState;
    productAvailability?: ProductAvailableQuantity[];
    addToCartErrorMessage?: string;
    addToCartSuccessMessage?: string;
    addToCartNeedsQtyMessage?: string;
    rsmAddToCartErrorMessageMinimumQty?: string;
    rsmAddToCartErrorMessageMultipleOf?: string;
    rsmContactHobartCustomerCare?: string;
    rsmItemOverweightMessage?: string;
    simpleProducts: SimpleProduct[];
}

/**
 * Returns the product inventory label.
 * @param  channelInventoryConfigurationId - The channel configuration Id.
 * @param  product - The product.
 * @returns The inventory label.
 */
export function getInventoryLabel(channelInventoryConfigurationId: number | undefined, product: ProductSearchResult): string | undefined {
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(product.AttributeValues)) {
        return undefined;
    }
    const inventoryAttribute = product.AttributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
}

export const ProductSearchResultItems: React.FC<IProductSearchResultItems> = ({
    simpleProducts,
    products,
    context,
    imageSettings,
    resources,
    moduleType,
    moduleId,
    allowBackNavigation,
    telemetryContent,
    quickviewSlot,
    productComparisonButton,
    channelInventoryConfigurationId,
    isPriceMinMaxEnabled,
    productsDimensionAvailabilities,
    cart,
    productAvailability,
    addToCartErrorMessage,
    addToCartSuccessMessage,
    addToCartNeedsQtyMessage,
    rsmAddToCartErrorMessageMinimumQty,
    rsmAddToCartErrorMessageMultipleOf,
    rsmContactHobartCustomerCare,
    rsmItemOverweightMessage
}) => {
    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator
    };
    const res: JSX.Element[] = [];
    products.forEach((product: ProductSearchResult, index: number) => {
        let currentProductAvailability: ProductAvailableQuantity | undefined;
        if (productAvailability) {
            currentProductAvailability = productAvailability.find(currentProduct => {
                return currentProduct.ProductId === product.RecordId;
            });
        }
        const simpleProduct: SimpleProduct | undefined = simpleProducts.find(currentProduct => {
            return currentProduct.RecordId === product.RecordId;
        });
        res.push(<li className='ms-product-search-result__item' key={index}>
            <ProductComponent
                context={context}
                telemetryContent={telemetryContent}
                imageSettings={imageSettings}
                freePriceText={resources.priceFree}
                originalPriceText={resources.originalPriceText}
                currentPriceText={resources.currentPriceText}
                ratingAriaLabel={resources.ratingAriaLabel}
                allowBack={allowBackNavigation}
                id={moduleId}
                key={product.RecordId}
                typeName={moduleType}
                data={{ product }}
                quickViewButton={quickviewSlot}
                productComparisonButton={productComparisonButton}
                inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product)}
                isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                priceResources={priceResources}
                swatchItemAriaLabel={resources.swatchItemAriaLabel}
                dimensionAvailabilities={productsDimensionAvailabilities?.find(
                    dimensionAvailability =>
                        ArrayExtensions.hasElements(dimensionAvailability) &&
                        dimensionAvailability[0].masterProductId === product.MasterProductId
                )}
                config={{
                    Cart: cart,
                    Availability: currentProductAvailability?.AvailableQuantity,
                    addToCartErrorMessage: addToCartErrorMessage,
                    addToCartSuccessMessage: addToCartSuccessMessage,
                    addToCartNeedsQtyMessage: addToCartNeedsQtyMessage,
                    currentSimpleProduct: simpleProduct,
                    rsmAddToCartErrorMessageMinimumQty: rsmAddToCartErrorMessageMinimumQty,
                    rsmAddToCartErrorMessageMultipleOf: rsmAddToCartErrorMessageMultipleOf,
                    rsmContactHobartCustomerCare: rsmContactHobartCustomerCare,
                    rsmItemOverweightMessage: rsmItemOverweightMessage
                }}
            />
        </li>
        );
    });
    return (
        <ul className='list-unstyled'>
            {res}
        </ul>
    );
};